import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useSession } from 'packages/@slerp/accounts'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import Loading from 'components/Utils/Loading'
import CommonHeader from '../Common/CommonHeader'
import ConfirmationModal, { ModalDescription } from '../ConfirmationModal'
import { ManageRoot } from '../styles'
import { PARTNER_SLUGS, SHIPDAY_HELP_URL } from '../../constants'
import EntryPage from './EntryPage'
import ConnectionsPage from './ConnectionsPage'
import ApiKeyStep from './ApiKeyStep'
import WebhookStep from './WebhookStep'
import IntegrationTypeStep from './IntegrationTypeStep'
import SelectStoresModal from './SelectStoresModal'
import {
  LIST_INTEGRATIONS,
  VALIDATE_INTEGRATION,
  SAVE_INTEGRATION,
  UPDATE_INTEGRATION,
  DISCONNECT_INTEGRATION
} from './queries'
import {
  ShipdayConnection,
  ShipdayIntegrationType,
  ShipdayIntegration,
  ValidationResult,
  toShipdayConnection
} from './types'

type ShipdayStep = 'entry' | 'connections' | 'api-key' | 'webhook' | 'type'

const PROVIDER = PARTNER_SLUGS.SHIPDAY

const ManageShipday = () => {
  const navigate = useNavigate()
  const { merchant } = useSession()
  const { merchantLocations } = useMerchant()
  const { addMessage } = useMessageStore()

  const [step, setStep] = useState<ShipdayStep>('entry')
  const [pendingApiKey, setPendingApiKey] = useState('')
  const [pendingWebhookUuid, setPendingWebhookUuid] = useState('')
  const [pendingWebhookUrl, setPendingWebhookUrl] = useState('')
  const [pendingToken, setPendingToken] = useState('')
  const [isSelectStoresModalOpen, setIsSelectStoresModalOpen] = useState(false)
  const [managingConnectionId, setManagingConnectionId] = useState<
    string | null
  >(null)
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([])
  const [skipTypeStep, setSkipTypeStep] = useState(false)

  const { data: listData, refetch: refetchIntegrations } = useQuery(
    LIST_INTEGRATIONS,
    {
      skip: !merchant?.id,
      variables: { merchant_id: merchant?.id, provider: PROVIDER },
      onError: () => {
        addMessage({ type: 'error', content: 'Failed to load integrations' })
      }
    }
  )

  const connections = useMemo<ShipdayConnection[]>(() => {
    const list: ShipdayIntegration[] = listData?.listIntegrations ?? []
    return list.map(toShipdayConnection)
  }, [listData])

  const managingConnection = useMemo(
    () => connections.find((c) => c.id === managingConnectionId) ?? null,
    [connections, managingConnectionId]
  )

  const newConnectionDisabledIds = useMemo(
    () =>
      connections.reduce<string[]>(
        (acc, c) => acc.concat(c.connectedStoreIds),
        []
      ),
    [connections]
  )

  const manageDisabledIds = useMemo(
    () =>
      connections
        .filter((c) => c.id !== managingConnectionId)
        .reduce<string[]>((acc, c) => acc.concat(c.connectedStoreIds), []),
    [connections, managingConnectionId]
  )

  const manageInitialIds = useMemo(() => {
    if (!managingConnection) return undefined
    return managingConnection.integrationType === 'merchant'
      ? merchantLocations.map((l) => l.id)
      : managingConnection.connectedStoreIds
  }, [managingConnection, merchantLocations])

  const isInitialLoading = !!merchant?.id && !listData
  const effectiveStep =
    step === 'entry' && connections.length > 0 ? 'connections' : step

  const [validateIntegration, { loading: isValidating }] = useMutation(
    VALIDATE_INTEGRATION,
    {
      onError: () => {
        addMessage({
          type: 'error',
          content: 'Invalid API key. Please check and try again.'
        })
      }
    }
  )

  const [saveIntegration, { loading: isSaving }] = useMutation(
    SAVE_INTEGRATION,
    {
      onError: () => {
        addMessage({
          type: 'error',
          content: 'Failed to save integration. Please try again.'
        })
      }
    }
  )

  const [updateIntegration, { loading: isUpdating }] = useMutation(
    UPDATE_INTEGRATION,
    {
      onError: () => {
        addMessage({
          type: 'error',
          content: 'Failed to update integration. Please try again.'
        })
      }
    }
  )

  const [disconnectIntegration] = useMutation(DISCONNECT_INTEGRATION, {
    onError: () => {
      addMessage({
        type: 'error',
        content: 'Failed to remove connection. Please try again.'
      })
    }
  })

  const handleEntryChoice = () => {
    setStep('connections')
  }

  const handleConnectAccount = () => {
    setSkipTypeStep(false)
    setStep('api-key')
  }

  const handleConnectStore = () => {
    setSkipTypeStep(true)
    setStep('api-key')
  }

  const handleApiKeyContinue = async (apiKey: string) => {
    const result = await validateIntegration({
      variables: {
        merchant_id: merchant?.id,
        provider: PROVIDER,
        api_key: apiKey
      }
    })
    const validation: ValidationResult | undefined =
      result.data?.validateIntegration
    if (!validation?.webhook_url) {
      addMessage({
        type: 'error',
        content: 'Invalid API key. Please check and try again.'
      })
      return
    }
    setPendingApiKey(apiKey)
    setPendingWebhookUuid(validation.webhook_uuid)
    setPendingWebhookUrl(validation.webhook_url)
    setPendingToken(validation.webhook_token)
    setStep('webhook')
  }

  const handleApiKeyCancel = () => {
    setStep('connections')
  }

  const handleWebhookContinue = () => {
    if (skipTypeStep) {
      setIsSelectStoresModalOpen(true)
    } else {
      setStep('type')
    }
  }

  const handleWebhookBack = () => {
    setStep('api-key')
  }

  const handleTypeContinue = (type: ShipdayIntegrationType) => {
    if (type === 'merchant') {
      saveConnection([])
    } else {
      setIsSelectStoresModalOpen(true)
    }
  }

  const handleTypeBack = () => {
    setStep('webhook')
  }

  const handleSelectStoresComplete = (storeIds: string[]) => {
    saveConnection(storeIds)
  }

  const handleSelectStoresBack = () => {
    setIsSelectStoresModalOpen(false)
  }

  const handleManage = (id: string) => {
    setManagingConnectionId(id)
  }

  const handleManageBack = () => {
    setManagingConnectionId(null)
  }

  const handleManageComplete = async (storeIds: string[], apiKey?: string) => {
    if (!managingConnectionId) return
    const isAllLocations =
      storeIds.length > 0 && storeIds.length === merchantLocations.length
    const result = await updateIntegration({
      variables: {
        merchant_id: merchant?.id,
        integration_id: managingConnectionId,
        api_key: apiKey,
        location_ids: isAllLocations ? undefined : storeIds
      }
    })
    if (!result.data?.updateIntegration) {
      addMessage({
        type: 'error',
        content: 'Failed to update integration. Please try again.'
      })
      return
    }

    await refetchIntegrations()
    setManagingConnectionId(null)
  }

  const saveConnection = async (locationIds: string[]) => {
    const isAllLocations =
      locationIds.length > 0 && locationIds.length === merchantLocations.length
    const result = await saveIntegration({
      variables: {
        merchant_id: merchant?.id,
        provider: PROVIDER,
        api_key: pendingApiKey,
        location_ids:
          locationIds.length === 0 || isAllLocations ? undefined : locationIds,
        webhook_uuid: pendingWebhookUuid || undefined
      }
    })
    if (!result.data?.saveIntegration) {
      addMessage({
        type: 'error',
        content: 'Failed to save integration. Please try again.'
      })
      return
    }

    await refetchIntegrations()
    setIsSelectStoresModalOpen(false)
    setPendingApiKey('')
    setPendingWebhookUuid('')
    setPendingWebhookUrl('')
    setPendingToken('')
    setStep('connections')
  }

  const handleDeleteMultiple = (ids: string[]) => {
    if (ids.length === 0) return
    setPendingDeleteIds(ids)
  }

  const handleConfirmDelete = async () => {
    const ids = pendingDeleteIds
    setPendingDeleteIds([])
    const results = await Promise.all(
      ids.map((id) =>
        disconnectIntegration({
          variables: { merchant_id: merchant?.id, integration_id: id }
        })
          .then(() => true)
          .catch(() => false)
      )
    )
    const failed = results.filter((ok) => !ok).length
    if (failed > 0) {
      addMessage({
        type: 'error',
        content: `Failed to disconnect ${failed} of ${ids.length} account${
          ids.length === 1 ? '' : 's'
        }`
      })
    }
    await refetchIntegrations()
  }

  const handleCancelDelete = () => {
    setPendingDeleteIds([])
  }

  if (!merchant?.id) return null

  return (
    <ManageRoot>
      <CommonHeader
        title='Connect Shipday'
        onBack={() => navigate('/integrations')}
        helpLink={SHIPDAY_HELP_URL}
      />

      {isInitialLoading ? (
        <Loading />
      ) : (
        <>
          {effectiveStep === 'entry' && (
            <EntryPage onContinue={handleEntryChoice} />
          )}

          {effectiveStep === 'connections' && (
            <ConnectionsPage
              connections={connections}
              onConnectAccount={handleConnectAccount}
              onConnectStore={handleConnectStore}
              onDeleteMultiple={handleDeleteMultiple}
              onManage={handleManage}
            />
          )}

          {effectiveStep === 'api-key' && (
            <ApiKeyStep
              stepNumber={1}
              loading={isValidating}
              onContinue={handleApiKeyContinue}
              onCancel={handleApiKeyCancel}
            />
          )}

          {effectiveStep === 'webhook' && (
            <WebhookStep
              stepNumber={2}
              webhookUrl={pendingWebhookUrl}
              token={pendingToken}
              onContinue={handleWebhookContinue}
              onBack={handleWebhookBack}
            />
          )}

          {effectiveStep === 'type' && (
            <IntegrationTypeStep
              stepNumber={3}
              loading={isSaving}
              onContinue={handleTypeContinue}
              onBack={handleTypeBack}
            />
          )}
        </>
      )}

      <SelectStoresModal
        idPrefix='shipday-new'
        isOpen={isSelectStoresModalOpen}
        loading={isSaving}
        locations={merchantLocations}
        disabledIds={newConnectionDisabledIds}
        storesLabel='Select stores to connect'
        onBack={handleSelectStoresBack}
        onComplete={handleSelectStoresComplete}
      />

      <ConfirmationModal
        title='Delete Shipday connection'
        confirmLabel='Delete'
        isOpen={pendingDeleteIds.length > 0}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      >
        <ModalDescription>
          {pendingDeleteIds.length === 1
            ? 'This Shipday account will be disconnected from your stores.'
            : `${pendingDeleteIds.length} Shipday accounts will be disconnected from your stores.`}
        </ModalDescription>
        <ModalDescription>
          Connected stores will lose access to Shipday dispatch. To resume, you
          will need to reconnect.
        </ModalDescription>
      </ConfirmationModal>

      <SelectStoresModal
        idPrefix='shipday-manage'
        isOpen={managingConnectionId !== null}
        loading={isUpdating}
        locations={merchantLocations}
        disabledIds={manageDisabledIds}
        initialIds={manageInitialIds}
        hasApiKey={!!managingConnection?.token}
        title='Manage connection'
        confirmLabel='SAVE CHANGES'
        onBack={handleManageBack}
        onComplete={handleManageComplete}
      />
    </ManageRoot>
  )
}

export default ManageShipday
